import AdminLayout from "@layouts/admin-layout";
import AppsLayout from "@layouts/apps-layout";
import AuthLayout from "@layouts/auth-layout";
import PrivateLayout from "@layouts/private-layout";
import PublicLayout from "@layouts/public-layout";
import FormStore from "@store/form.store";
import { lazy } from "react";
import { Provider } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
const SignUpPage = lazy(() => import("@pages/auth/singup"));
const ForgotPasswordPage = lazy(() => import("@pages/auth/forgot-password"));
const OTPPage = lazy(() => import("@pages/auth/otp"));
const SettingsPage = lazy(() => import("@pages/settings"));
const PasswordSettingsPage = lazy(() => import("@pages/settings/password"));
const ProfileSettingsPage = lazy(() => import("@pages/settings/profile"));
const ManageFormsPage = lazy(() => import("@pages/manage-forms"));
const ManageCategoryPage = lazy(() => import("@pages/manage-categories"));
const PreviewPage = lazy(() => import("@pages/studio/preview"));
const ManageUsersPage = lazy(() => import("@pages/manage-users"));
const OrganizationSettingsPage = lazy(() => import("@pages/settings/organization"));
const ManageEntitiesPage = lazy(() => import("@pages/manage-entities"));
const MenuResponsesPage = lazy(() => import("@pages/menu-responses"));
const NewPasswordPage = lazy(() => import("@pages/auth/new-password"));
const ManageGroupsPage = lazy(() => import("@pages/manage-groups"));
const MarketPlacePage = lazy(() => import("@pages/market-place"));
const MarketPlaceApp = lazy(() => import("@pages/market-place/marketplace-app"));
const ResponseTemplatePage = lazy(() => import("@pages/studio/form-data-views"));
const LayoutPage = lazy(() => import("@pages/studio/form-layout"));
const FormDesignerPage = lazy(() => import("@pages/studio/form-designer"));
const MenuFormPage = lazy(() => import("@pages/menu-form"));
const FormResponsesPage = lazy(() => import("@pages/studio/form-responses"));
const FormRulesPage = lazy(() => import("@pages/studio/form-rule"));
const ApiRequestPage = lazy(() => import("@pages/api-request"));
const ErrorPage = lazy(() => import("@pages/error"));
const ManageAppsPage = lazy(() => import("@pages/manage-apps"));
const AppsPage = lazy(() => import("@pages/apps"));
const LoginPage = lazy(() => import("@pages/auth/signin"));
const MaintenancePage = lazy(() => import("@pages/maintenance-page"));
const LayoutDesignerPage = lazy(() => import("@pages/studio/layout"));
const ManageDashBoardPage = lazy(() => import("@pages/manage-dashboard"));
const DashboardsDesignPage = lazy(() => import("@pages/dashboard-designer"));
const FormWidgetPage = lazy(() => import("@pages/studio/form-widgets"));
const MenuDashboardPage = lazy(() => import("@pages/menu-dashboard"));

import { useFeatureFlagEnabled } from "posthog-js/react";
import { VersionPopup } from "@components/version-popup";
import { AppLiveProvider } from "@contexts/live-app.context";

export default function RoutesData() {
  const isMaintenanceMode = useFeatureFlagEnabled("maintenance-mode");

  if (isMaintenanceMode) {
    return (
      <>
        <Routes>
          <Route path="*" element={<MaintenancePage />} />
        </Routes>
        <VersionPopup />
      </>
    );
  }

  return (
    <>
      <Routes>
        <Route path="*" element={<ErrorPage />} />
        <Route index element={<Navigate to="/apps" />} />
        <Route path="auth" element={<AuthLayout />}>
          <Route index element={<LoginPage />} />
          <Route path="signin" element={<LoginPage />} />
          <Route path="signup" element={<SignUpPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="otp" element={<OTPPage />} />
          <Route path="new-password" element={<NewPasswordPage />} />
        </Route>
        <Route element={<PrivateLayout />}>
          <Route path="builder" element={<AdminLayout />}>
            <Route index element={<ManageAppsPage />} />
            <Route path="apps" element={<ManageAppsPage />} />
            <Route path="market-place" element={<MarketPlacePage />} />
            <Route path="market-place/:appId" element={<MarketPlaceApp />} />
            <Route path="categories" element={<ManageCategoryPage />} />
            <Route path="users" element={<ManageUsersPage />} />
            <Route path="groups" element={<ManageGroupsPage />} />
            <Route path="entities" element={<ManageEntitiesPage />} />
            <Route path="apps/:appId/forms" element={<ManageFormsPage />} />
            <Route path="apps/:appId/dashboards/:dashboardId" element={<DashboardsDesignPage />} />
            <Route path="apps/:appId/dashboards" element={<ManageDashBoardPage />} />
            <Route
              path="apps/:appId/forms/:formId/design"
              element={
                <Provider store={FormStore}>
                  <FormDesignerPage />
                </Provider>
              }
            />
            <Route path="apps/:appId/forms/:formId/templates" element={<ResponseTemplatePage />} />
            <Route path="apps/:appId/forms/:formId/layouts" element={<LayoutPage />} />
            <Route
              path="apps/:appId/forms/:formId/layouts/:layoutId/templates/:responseTemplateId"
              element={<LayoutDesignerPage />}
            />
            <Route path="apps/:appId/forms/:formId/rules" element={<FormRulesPage />} />
            <Route
              path="apps/:appId/forms/:formId/preview"
              element={
                <Provider store={FormStore}>
                  <PreviewPage />
                </Provider>
              }
            />
            <Route path="apps/:appId/forms/:formId/responses" element={<FormResponsesPage />} />
            <Route path="apps/:appId/forms/:formId/widgets" element={<FormWidgetPage />} />
            <Route
              path="apps/:appId/forms/:formId/responses/:responseTemplateId"
              element={<FormResponsesPage />}
            />
            <Route path="settings" element={<SettingsPage baseUrl="/builder" />}>
              <Route index element={<ProfileSettingsPage />} />
              <Route path="profile" element={<ProfileSettingsPage />} />
              <Route path="password" element={<PasswordSettingsPage />} />
              <Route
                path="organization"
                element={<OrganizationSettingsPage baseUrl="/builder" />}
              />
            </Route>
          </Route>
          <Route
            path="apps"
            element={
              <AppLiveProvider>
                <AppsLayout />
              </AppLiveProvider>
            }
          >
            <Route index element={<AppsPage />} />
            <Route
              path=":appId/forms/:formId"
              element={
                <Provider store={FormStore}>
                  <MenuFormPage />
                </Provider>
              }
            />
            <Route path=":appId/dashboards/:dashboardId" element={<MenuDashboardPage />} />
            <Route path=":appId/api-call" element={<ApiRequestPage />} />
            <Route
              path=":appId/forms/:formId/responses/:responseTemplateId"
              element={<MenuResponsesPage />}
            />
            <Route path="settings" element={<SettingsPage baseUrl="/apps" />}>
              <Route index element={<ProfileSettingsPage />} />
              <Route path="profile" element={<ProfileSettingsPage />} />
              <Route path="password" element={<PasswordSettingsPage />} />
              <Route path="organization" element={<OrganizationSettingsPage baseUrl="/apps" />} />
            </Route>
          </Route>
        </Route>
        <Route path="public" element={<PublicLayout />}>
          <Route
            path="forms/:appId/:formId/:distributionId"
            element={
              <Provider store={FormStore}>
                <MenuFormPage />
              </Provider>
            }
          />
        </Route>
      </Routes>
      <VersionPopup />
    </>
  );
}
